<template>
	<div class="select" :class="[checkIcon]">
		<div v-if="label" class="label">{{ label }}</div>
		<div class="items">
			<div
				v-for="option in options"
				:key="option.label"
				@click="handleClick(option)"
				class="item"
				:class="[type, option.selected ? 'selected' : '', theme]"
			>
				<div class="item--label">{{ option.label }}</div>
				<svg
					v-if="isMultiple && checkIcon === 'check-circle'"
					class="item--checked"
					viewBox="0 0 22 22"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<circle cx="11" cy="11" r="11" fill="white" />
					<path
						d="M15.675 7.1499L11.3653 14.0933C11.0335 14.6279 10.2963 14.7268 9.83524 14.2987L6.875 11.5499"
						stroke="#7D8BF7"
						stroke-width="3"
						stroke-linecap="round"
					/>
				</svg>

				<svg
					v-else-if="isMultiple && checkIcon === 'check-square'"
					class="item--checked"
					width="24"
					height="24"
					viewBox="0 0 24 24"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<rect width="24" height="24" rx="6" fill="#56A97A" />
					<path d="M7 12.3476L10.4048 15.6476L17 9" stroke="white" stroke-width="2" stroke-linecap="round"/>
				</svg>

				<svg v-else class="arrow--right" height="48" viewBox="0 0 48 48" width="48" xmlns="http://www.w3.org/2000/svg">
					<path d="M17.17 32.92l9.17-9.17-9.17-9.17 2.83-2.83 12 12-12 12z" />
					<path d="M0-.25h48v48h-48z" fill="none" />
				</svg>
			</div>
		</div>
	</div>
</template>

<script>
import { computed } from '@vue/reactivity'
export default {
	props: {
		options: {
			required: true
		},
		type: {
			default: 'single'
		},
		label: {
			default: null
		},
		theme: {
			default: null
		},
		checkIcon: {
			type: String,
			validator(value) {
				return ['check-circle', 'check-square'].includes(value)
			},
			default: 'check-circle'
		}
	},

	emits: ['change'],

	setup(props, { emit }) {
		const selected = computed(() => props.options.filter((option) => option.selected))

		const isMultiple = computed(() => props.type === 'multiple')

		const handleClick = (option) => {
			let options = selected.value.map((item) => item.label)

			if (props.type == 'single') {
				options = [option.label]
			} else {
				const index = selected.value.findIndex((item) => {
					return item.label == option.label
				})

				if (index === -1) {
					options.push(option.label)
				} else {
					options.splice(index, 1)
				}
			}

			emit('change', options)
		}

		return {
			handleClick,
			isMultiple
		}
	}
}
</script>

<style lang="scss" scoped>
.select {
	margin-top: 2rem;

	&.check-square {
		.item::after {
			content: '';
			position: absolute;
			right: var(--survey-select-checked-right, 1rem);
			left: var(--survey-select-checked-left, auto);
			top: 50%;
			transform: translateY(-50%);
			opacity: 0;
			width: calc(1.5rem - 1px);
			transition: opacity 0.3s;
			height: calc(1.5rem - 1px);
			border-radius: 6px;
			border: 1px solid #979aaa;
		}
		.item:not(.selected)::after {
			opacity: 1;
		}
	}
}

.label {
	margin-bottom: 0.8rem;
	font-size: 1.11rem;
}
.item {
	margin-bottom: var(--survey-select-item-margin-bottom, 0.5rem);
	display: flex;
	position: relative;
	background-color: var(--survey-select-item-background-color, #ededf3);
	min-height: var(--survey-select-item-min-height, 4rem);
	align-items: center;
	padding: var(--survey-select-item-padding, 0.9rem 3.5rem 0.9rem 1rem);
	border: var(--survey-select-item-border, 1px solid #ededf3);
	border-radius: 0.8rem;
	color: var(--survey-select-item-color, #2a2351);
	font-size: 1.1rem;
	line-height: 1.26;
	font-weight: 500;
	cursor: pointer;
	transition: background-color 0.3s, color 0.3s, border 0.3s;

	&--checked {
		position: absolute;
		right: var(--survey-select-checked-right, 1rem);
		left: var(--survey-select-checked-left, auto);
		top: 50%;
		transform: translateY(-50%);
		width: 1.5rem;
		opacity: 0;
		transition: opacity 0.3s;
	}

	.arrow--right {
		width: 26px;
		position: absolute;
		right: 1rem;
		top: 50%;
		transform: translateY(-50%);
		fill: #424982;
	}

	&.light {
		background-color: #fff;
		box-shadow: 1px 1px 10px rgba(0, 0, 0, 0.08);
		color: #424982;
	}

	&.large {
		min-height: 5rem;
		margin-bottom: 1rem;
	}

	&.selected {
		background-color: var(--survey-select-item-active-background-color, #7d8bf7);
		color: var(--survey-select-color-active, #fff);
		border: var(--survey-select-item-border-active, 1px solid #ededf3);

		.arrow--right {
			fill: #fff;
		}
	}

	&.selected & {
		&--checked {
			opacity: 1;
		}
	}
}
</style>
